import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Copy, RefreshCw, Zap, ClipboardList, KeyRound, Radio } from "lucide-react";

export default function EliteBase() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/elitebase/events"); setEvents(data); }
    catch { setEvents([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <PageHeader title="EliteBase integration" subtitle="API key rotation, usage logs, live sync and access commands. Both apps live on Emergent — connect via API key." />
      <PageBody>
        <Tabs defaultValue="api-key" className="w-full">
          <TabsList className="mb-4 flex-wrap gap-1" data-testid="elitebase-tabs">
            <TabsTrigger value="api-key" data-testid="tab-api-key"><KeyRound className="w-3.5 h-3.5 mr-1.5" />API key</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs"><ClipboardList className="w-3.5 h-3.5 mr-1.5" />Usage log</TabsTrigger>
            <TabsTrigger value="live-sync" data-testid="tab-live-sync"><Zap className="w-3.5 h-3.5 mr-1.5" />Live sync</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events"><Radio className="w-3.5 h-3.5 mr-1.5" />Onboarding</TabsTrigger>
          </TabsList>

          <TabsContent value="api-key"><ApiKeyPanel /></TabsContent>
          <TabsContent value="logs"><UsageLogsPanel /></TabsContent>
          <TabsContent value="live-sync"><LiveSyncPanel /></TabsContent>
          <TabsContent value="events">
            <div className="mb-4 border border-blue-200 bg-blue-50 p-4 rounded-md flex justify-between items-center gap-4" data-testid="elitebase-student-integration-notice">
              <div>
                <h2 className="text-base font-semibold text-blue-950">Student profile synchronization</h2>
                <p className="text-xs text-blue-800 mt-1">Human-reviewed source updates and sync requests live in Students. This is separate from onboarding schedules below.</p>
              </div>
              <div className="flex gap-2">
                <Button asChild variant="outline"><Link to="/students?tab=updates" data-testid="elitebase-student-queue-link">Open student review queue</Link></Button>
                <Button onClick={() => setOpen(true)} data-testid="access-command-button">Send access command</Button>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Kind</TableHead><TableHead>Received</TableHead><TableHead>External / Sale</TableHead><TableHead>Details</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {events.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No EliteBase events yet.</TableCell></TableRow>}
                  {events.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-xs">{e.kind}</TableCell>
                      <TableCell className="text-xs font-mono">{fmtDateTime(e.created_at)}</TableCell>
                      <TableCell className="text-xs font-mono">{e.external_id || e.sale_number || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-md truncate">
                        {e.kind === "onboarding" && e.payload && `${e.payload.learner_name} · ${e.payload.programme_code}`}
                        {e.kind === "access_command" && `${e.action.toUpperCase()} · ${e.reason || ""}`}
                      </TableCell>
                      <TableCell>
                        {e.kind === "onboarding" && <Badge variant="outline" className={e.processed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{e.processed ? "PROCESSED" : "PENDING"}</Badge>}
                        {e.kind === "access_command" && <Badge variant="outline" className={e.acknowledged ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-700 border-slate-200"}>{e.acknowledged ? "ACK" : "QUEUED"}</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>

      <AccessDialog open={open} onOpenChange={setOpen} onDone={() => { setOpen(false); load(); }} />
    </>
  );
}

// -------------------- API key panel --------------------
function ApiKeyPanel() {
  const [status, setStatus] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/elitebase/settings/api-key"); setStatus(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    setRotating(true);
    try {
      const { data } = await api.post("/elitebase/settings/api-key/rotate");
      setNewKey(data.key);
      setConfirmOpen(false);
      toast.success("API key rotated. Copy it now — it will not be shown again.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setRotating(false); }
  };

  const copy = (value, label) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3" data-testid="api-key-status-card">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Current API key</h3>
          {status && <Badge variant="outline" className={status.source === "database" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700"}>{status.source}</Badge>}
        </div>
        <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded p-3" data-testid="api-key-masked">
          {status ? (status.has_key ? status.masked : "— no key configured —") : "…"}
        </div>
        <div className="text-xs text-slate-500">
          {status?.rotated_at ? (
            <>Last rotated <span className="font-mono">{fmtDateTime(status.rotated_at)}</span></>
          ) : (
            <>Never rotated — key is being read from environment. Rotate now to bring it under UI control.</>
          )}
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={() => setConfirmOpen(true)} data-testid="rotate-api-key-button">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Rotate key
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">How to use</h3>
        <ol className="text-xs text-slate-600 space-y-1 list-decimal ml-4">
          <li>Send <code className="font-mono bg-slate-100 px-1">X-API-Key: &lt;key&gt;</code> header</li>
          <li>GET <code className="font-mono">/api/elitebase/student-records?email=…</code></li>
          <li>Returns Approved / Sent invoices + verified receipts for that learner</li>
          <li>Every request is logged in the Usage log tab</li>
        </ol>
        <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-100">
          Rotate this key any time — the old key stops working immediately.
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rotate EliteBase API key?</DialogTitle>
            <DialogDescription>The current key stops working immediately. You must copy the new key into EliteBase before it can call EliteOS again.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={rotate} disabled={rotating} data-testid="rotate-api-key-confirm">{rotating ? "Rotating…" : "Rotate now"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New EliteBase API key</DialogTitle>
            <DialogDescription>Copy this key now — it will not be shown again. Paste it into EliteBase's outbound API settings.</DialogDescription>
          </DialogHeader>
          <div className="font-mono text-xs bg-slate-900 text-emerald-300 rounded p-3 break-all" data-testid="new-api-key-display">{newKey}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => copy(newKey, "API key")} data-testid="copy-new-api-key">
              <Copy className="w-4 h-4 mr-1.5" /> Copy
            </Button>
            <Button onClick={() => setNewKey(null)}>I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------------------- Usage logs panel --------------------
function UsageLogsPanel() {
  const [rows, setRows] = useState([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (emailFilter) params.email = emailFilter;
      if (statusFilter && statusFilter !== "all") params.status_code = statusFilter;
      const { data } = await api.get("/elitebase/logs", { params });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  }, [emailFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs uppercase text-slate-500">Email contains</Label>
          <Input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="student@example.com" data-testid="logs-email-filter" />
        </div>
        <div className="w-40">
          <Label className="text-xs uppercase text-slate-500">Status</Label>
          <Select value={statusFilter || "all"} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="logs-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="200">200 OK</SelectItem>
              <SelectItem value="401">401 Unauthorized</SelectItem>
              <SelectItem value="422">422 Bad request</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="logs-refresh">Refresh</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Invoices</TableHead>
              <TableHead>Receipts</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="logs-body">
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6">No API calls yet.</TableCell></TableRow>}
            {rows.map((l) => (
              <TableRow key={l.id} data-testid={`log-row-${l.id}`}>
                <TableCell className="text-xs font-mono">{fmtDateTime(l.timestamp)}</TableCell>
                <TableCell className="text-xs">{l.email || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    l.status_code === 200 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : l.status_code >= 400 ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-slate-50 text-slate-700"}>{l.status_code}</Badge>
                </TableCell>
                <TableCell className="text-xs font-mono">{l.record_count?.invoices ?? "—"}</TableCell>
                <TableCell className="text-xs font-mono">{l.record_count?.receipts ?? "—"}</TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{l.ip || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{l.error || ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// -------------------- Live sync panel --------------------
function LiveSyncPanel() {
  const [cfg, setCfg] = useState({ enabled: false, target_url: "", signing_secret: "", has_secret: false, masked_secret: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: ev }] = await Promise.all([
        api.get("/elitebase/settings/live-sync"),
        api.get("/elitebase/live-sync/outbound", { params: { limit: 50 } }),
      ]);
      setCfg({ enabled: c.enabled, target_url: c.target_url || "", signing_secret: "",
               has_secret: c.has_secret, masked_secret: c.masked_secret,
               updated_at: c.updated_at });
      setEvents(ev);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled: cfg.enabled, target_url: cfg.target_url };
      if (cfg.signing_secret) body.signing_secret = cfg.signing_secret;
      await api.put("/elitebase/settings/live-sync", body);
      toast.success("Live sync settings saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const testPing = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/elitebase/settings/live-sync/test");
      if (data.status === "delivered") toast.success(`Ping delivered (HTTP ${data.http_status})`);
      else if (data.status === "skipped") toast.warning("Live sync disabled — enable and save first");
      else toast.error(`Delivery failed: ${data.error || data.http_status}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-4" data-testid="live-sync-config">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Outbound webhook</h3>
            <p className="text-xs text-slate-500 mt-1">Push invoice.approved, invoice.sent and receipt.verified events to EliteBase within seconds of the operator action.</p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="live-sync-enabled" className="text-xs uppercase text-slate-500">Enabled</Label>
            <Switch id="live-sync-enabled" checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="live-sync-enabled" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase text-slate-500">Target URL</Label>
          <Input value={cfg.target_url} onChange={(e) => setCfg({ ...cfg, target_url: e.target.value })} placeholder="https://elitebase.example.com/webhooks/eliteos" className="font-mono text-xs" data-testid="live-sync-url" />
          <p className="text-[11px] text-slate-400 mt-1">EliteOS will POST the JSON event envelope here and sign it with <code className="font-mono">X-EliteOS-Signature: sha256=…</code>.</p>
        </div>

        <div>
          <Label className="text-xs uppercase text-slate-500">Signing secret {cfg.has_secret && <span className="ml-2 font-mono normal-case text-slate-500">stored: {cfg.masked_secret}</span>}</Label>
          <Input type="password" value={cfg.signing_secret} onChange={(e) => setCfg({ ...cfg, signing_secret: e.target.value })} placeholder={cfg.has_secret ? "Leave blank to keep the saved secret" : "shared_secret_used_to_hmac_the_body"} className="font-mono text-xs" data-testid="live-sync-secret" />
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={save} disabled={saving} data-testid="live-sync-save">{saving ? "Saving…" : "Save"}</Button>
          <Button variant="outline" onClick={testPing} disabled={testing} data-testid="live-sync-test">{testing ? "Sending…" : "Send test ping"}</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Recent outbound deliveries</h3>
          <Button variant="ghost" size="sm" onClick={load} data-testid="outbound-refresh">Refresh</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Event</TableHead><TableHead>Status</TableHead><TableHead>HTTP</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
          <TableBody data-testid="outbound-body">
            {events.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No deliveries yet.</TableCell></TableRow>}
            {events.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="text-xs font-mono">{fmtDateTime(e.created_at)}</TableCell>
                <TableCell className="text-xs font-mono">{e.event_type}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    e.status === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : e.status === "failed" ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-slate-50 text-slate-700 border-slate-200"}>{e.status}</Badge>
                </TableCell>
                <TableCell className="text-xs font-mono">{e.http_status ?? "—"}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-md truncate">{e.error || ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// -------------------- Access command dialog (unchanged) --------------------
function AccessDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ sale_number: "", action: "restrict", reason: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.sale_number) { toast.error("Enter a sale number"); return; }
    setSaving(true);
    try { await api.post("/elitebase/access-command", form); toast.success("Command queued"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Send access command to EliteBase</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase">Sale number</Label><Input value={form.sale_number} onChange={(e) => setForm({ ...form, sale_number: e.target.value })} className="font-mono" data-testid="access-sale" /></div>
          <div><Label className="text-xs uppercase">Action</Label>
            <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="restrict">Restrict</SelectItem><SelectItem value="release">Release</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase">Reason</Label><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="access-submit">{saving ? "Queuing…" : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
